import * as echarts from "echarts/core";
import type { CallbackDataParams } from "echarts/types/dist/shared";
import { getUnitCategoryName } from "verdi-common/i18n";
import { formatTooltipValue } from "../../format";
import type { GetDisplayInfo } from "../../units/use-get-display-info";
import type { PreparedSeries } from "../prepare-series";

export type TooltipContext = {
    seriesByStreamId: Map<number, PreparedSeries>;
    getDisplayInfo: GetDisplayInfo;
    showDeviceName: boolean;
};

const buildTooltipRowHtml = (
    preparedSeries: PreparedSeries | undefined,
    data: CallbackDataParams,
    getDisplayInfo: GetDisplayInfo,
    showDeviceName: boolean,
): string => {
    const marker = String(data.marker ?? "");
    const unitCategory = preparedSeries?.unitCategory ?? "";
    const category = getUnitCategoryName(unitCategory);
    const value = (Array.isArray(data.value) ? data.value[1] : data.value) as number | null;
    const unitSuffix = unitCategory ? getDisplayInfo(unitCategory).symbol : "";
    const formattedValue =
        value === null || typeof value === "undefined"
            ? "—"
            : [formatTooltipValue(value), unitSuffix].filter(Boolean).join(" ");

    // biome-ignore format: keep tooltip HTML indentation
    return (
        `<div style="display: flex; justify-content: space-between; gap: 16px;">` +
            `<div style="display: flex; gap: 4px;">` +
                `<div>${marker}</div>` +
                `<div style="display: flex; flex-direction: column; gap: 2px; align-items: flex-start; justify-content: flex-start; flex: 0; flex-grow: 0;">` +
                    `<div>${echarts.format.encodeHTML(category)}</div>` +
                    `${showDeviceName ? `<div style="font-size: 10px; line-height: 12px; color: gray;">${echarts.format.encodeHTML(preparedSeries?.name ?? "")}</div>` : ""}` +
                `</div>` +
            `</div>` +
            `<div style="display: flex; justify-content: flex-end;">${formattedValue}</div>` +
        `</div>`
    );
};

/** One row per hovered series: marker, unit category over device name, then the value. */
export function renderTooltipHtml(params: CallbackDataParams[], tooltipContext: TooltipContext): string {
    const rows = params
        .map((p) =>
            buildTooltipRowHtml(
                tooltipContext.seriesByStreamId.get(Number(p.seriesId)),
                p,
                tooltipContext.getDisplayInfo,
                tooltipContext.showDeviceName,
            ),
        )
        .join("");

    return `<div style="display: flex; flex-direction: column; gap: 6px;">${rows}</div>`;
}
